import './App.css';

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import questionData from './questiondata.json'
import fakeScores from './fakescores.json'
import TimerComponent from './components/TimerComponent'
import useTimeout from './hooks/useTimeout'
import timerBkgImg from './img/timer.webp'

//Motion.dev
import { easeIn, easeOut, motion, useAnimate } from "motion/react"


function App() {

  //Constants
  //Do not change these - These will work for dev, stage, and production
  const submitScoresEndpoint = "https://yaa7vllbmg.execute-api.us-east-1.amazonaws.com/Development/submitscore"
  const getScoresEndpoint = "https://yaa7vllbmg.execute-api.us-east-1.amazonaws.com/Development/getscores"

  //Update the table to match the environment you want to run in.
  const tableName = "wakix_fastfacts_production"

  //Session timeout - Default 90 seconds. On timeout, Resets to home.
  const timeoutSeconds = 90;

  //Session Times up - Shows times up screen - 60 seconds of playable time.
  const timerSeconds = 60;

  const [enableTouch, setEnableTouch] = useState(true)

  const [userName, setUserName] = useState('');
  const [userNameStatus, setUserNameStatus] = useState('default');

  const [userScore, setUserScore] = useState(0);
  const [userTimeBonus, setUserTimeBonus] = useState(0);
  const [scoreboard, setScoreboard] = useState([])

  const [sessionStep, setSessionStep] = useState(0);
  const [unusedQuestions, setUnusedQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({});

  const [gameState, setGameState] = useState("pregame");
  const [showSecretMenu, setShowSecretMenu] = useState(false);

  //Reference to the timer.
  const timerRef = useRef(null);

  //Animation Refs
  const [step0_play_ref, step0_play_animate] = useAnimate();
  const [step0_fastfacts_ref, step0_fastfacts_animate] = useAnimate();
  const [step0_getstarted_ref, step0_getstarted_animate] = useAnimate();

  const [step1_play_ref, step1_play_animate] = useAnimate();
  const [step1_1try_ref, step1_1try_animate] = useAnimate();
  const [step1_2tries_ref, step1_2tries_animate] = useAnimate();
  const [step1_3tries_ref, step1_3tries_animate] = useAnimate();
  const [step1_4tries_ref, step1_4tries_animate] = useAnimate();

  const [step1_inputblock_ref, step1_inputblock_animate] = useAnimate();

  const [step2_3_ref, step2_3_animate] = useAnimate();
  const [step2_2_ref, step2_2_animate] = useAnimate();
  const [step2_1_ref, step2_1_animate] = useAnimate();
  const [step2_go_ref, step2_go_animate] = useAnimate();

  const [step8_continue_ref, step8_continue_animate] = useAnimate();

  const [step8_timesup1_ref, step8_timesup1_animate] = useAnimate();
  const [step8_timesup2_ref, step8_timesup2_animate] = useAnimate();
  const [step10_seehowyoudid_ref, step10_seehowyoudid_animate] = useAnimate();

  const [answers_scope_ref, answers_scope_animate] = useAnimate();

  const [step8_greenplate_ref, step8_greenplate_animate] = useAnimate();

  const [step11_home_ref, step11_home_animate] = useAnimate();
  const [step11_playagain_ref, step11_playagain_animate] = useAnimate();

  const [step0_background_ref, step0_background_animate] = useAnimate();
  const [step1_background_ref, step1_background_animate] = useAnimate();
  const [step2_background_ref, step2_background_animate] = useAnimate();
  const [step8_background_ref, step8_background_animate] = useAnimate();
  const [step10_background_ref, step10_background_animate] = useAnimate();
  const [step11_background_ref, step11_background_animate] = useAnimate();

  const [timerbkg_ref, timerbkg_animate] = useAnimate();
  const [timesup_container_ref, timesup_container_animate] = useAnimate();
  const [timesup_plate_ref, timesup_plate_animate] = useAnimate();

  //This block will hide the keyboard if they hit the enter button on the keyboard.
  const userNameInput = document.getElementById('usernameinput');



  //Timeout
  const timeoutTimerEnded = () => {

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'session_timeout', {
      });
    } else {
      console.log('gtag not available');
    }

    setTimeout(() => {
      setSessionStep(0);
      timerRef.current.reset();
      timerRef.current.hide();
      resetUser();
      resetGame();
    }, 50);
  }


  const timeoutTimer = useTimeout(timeoutSeconds, timeoutTimerEnded)



  /* 
    Create Step 0 Animation. This will run every time sessionStep changes to 0 including at launch.
    useEffect watches for changes in sessionStep. If it hits zero, this will run.
    Ensures animation sequence plays any time we re-cycle to home screen.
  */

  useEffect(() => {
    const animateIntroSequence = async () => {
      if (step0_play_ref.current) {
        await delay(1750);

        //Reset Play text
        await step0_play_animate(
          step0_play_ref.current,
          { x: 1000, filter: "blur(5px)" },
          { duration: 0 }
        )

        //Reset Fast Facts text
        await step0_fastfacts_animate(
          step0_fastfacts_ref.current,
          { x: 1000, filter: "blur(5px)" },
          { duration: 0 }
        )

        await step0_getstarted_animate(
          step0_getstarted_ref.current,
          { scale: 0.0 },
          { duration: 0 }
        )

        //Play animation
        step0_play_animate(
          step0_play_ref.current,
          { x: 0, filter: "blur(0px)" },
          {
            type: "spring",
            stiffness: 500,
            damping: 25
          }
        )


        //FastFacts Anim
        await step0_fastfacts_animate(
          step0_fastfacts_ref.current,
          { x: 0, filter: "blur(0px)" },
          {
            type: "spring",
            delay: 0.45,
            stiffness: 500,
            damping: 25
          }
        )

        await delay(500);

        await step0_getstarted_animate(
          step0_getstarted_ref.current,
          { opacity: 1, scale: 1 },
          {
            type: "spring",
            delay: 0.0,
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )
        setTimeout(() => {
          pulseStep1GetStartedButton();
        }, 10000)
      }
    };

    if (sessionStep == 0) {
      setTimeout(() => {
        animateIntroSequence();
      }, 150)
    }
  }, [sessionStep, step0_play_animate, step0_play_ref, step0_fastfacts_animate, step0_fastfacts_ref, step0_getstarted_animate, step0_getstarted_ref])





  /* 
    Create Step 1 Animation. This will run every time sessionStep changes to 1 including at launch.
    useEffect watches for changes in sessionStep. If it hits 1, this will run.
    Ensures animation sequence plays any time we re-cycle to input name screen.
  */
  useEffect(() => {
    const animateStep1 = async () => {
      if (step1_1try_ref.current && step1_2tries_ref.current && step1_3tries_ref.current && step1_4tries_ref.current) {
        console.log("UseEffect - Step 1 - Run animations");
        //Reset 1 try text
        await delay(500);

        await step1_1try_animate(
          step1_1try_ref.current,
          { scale: 0, opacity: 0 },
          { duration: 0 }
        )

        //Reset 2 tries text
        await step1_2tries_animate(
          step1_2tries_ref.current,
          { scale: 0, opacity: 0 },
          { duration: 0 }
        )

        //Reset 3 tries text
        await step1_3tries_animate(
          step1_3tries_ref.current,
          { scale: 0, opacity: 0 },
          { duration: 0 }
        )

        //Reset 4 tries text
        await step1_4tries_animate(
          step1_4tries_ref.current,
          { scale: 0, opacity: 0 },
          { duration: 0 }
        )

        //Now, stagger the animations so they appear on properly.
        step1_1try_animate(
          step1_1try_ref.current,
          { scale: 1, opacity: 1 },
          {
            type: "spring",
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )

        step1_2tries_animate(
          step1_2tries_ref.current,
          { scale: 1, opacity: 1 },
          {
            type: "spring",
            delay: 0.15,
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )

        step1_3tries_animate(
          step1_3tries_ref.current,
          { scale: 1, opacity: 1 },
          {
            type: "spring",
            delay: 0.25,
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )

        step1_4tries_animate(
          step1_4tries_ref.current,
          { scale: 1, opacity: 1 },
          {
            type: "spring",
            delay: 0.35,
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )
      }
    }

    if (sessionStep == 1) {
      animateStep1();
    }

  }, [sessionStep, step1_1try_ref, step1_2tries_ref, step1_3tries_ref, step1_4tries_ref, step1_1try_animate, step1_2tries_animate, step1_3tries_animate, step1_4tries_animate]);






  const pulseStep1GetStartedButton = async () => {
    if (sessionStep == 0) {
      if (step0_getstarted_ref.current) {

        await step0_getstarted_animate(
          step0_getstarted_ref.current,
          { scale: 0.0, opacity: 0.0 },
          { duration: 0.1 }
        )

        await delay(100);

        await step0_getstarted_animate(
          step0_getstarted_ref.current,
          { opacity: 1, scale: 1 },
          {
            type: "spring",
            delay: 0.0,
            bounce: 1.0,
            visualDuration: 0.01,
            stiffness: 500,
            damping: 15,
            mass: 1.2
          }
        )

        if (sessionStep == 0) {
          setTimeout(() => {
            pulseStep1GetStartedButton();
          }, 10000)
        }
      }
    }
  }


  //Run this at start - 
  //Dismiss Keyboard if the user presses the enter button in either input field.
  if (userNameInput) {
    userNameInput.addEventListener('keyup', function (event) {
      if (event.key === 'Enter' || event.keyCode === 13) {
        userNameInput.blur();
      }
    })
  }

  /* Backend communication functions. */
  const submit_score_to_backend = async (name, score) => {
    const scorePayload = {
      tablename: tableName,
      name: name,
      score: score
    };

    try {
      const response = await fetch(submitScoresEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scorePayload),
      });

      const responseData = await response.json(); // Assuming your Lambda sends back JSON

      if (response.ok) {
        console.log("Score submitted successfully!", responseData);
        // Maybe set a success message for the user!
      } else {
        console.error("Failed to submit score:", responseData);
        // Maybe set an error message for the user.
      }
    } catch (error) {
      console.error("Network error or other issue:", error);
      // Handle network errors, etc.
    }

  }

  const get_scores_from_backend = async (e) => {
    console.log("get_scores_from_backend - entered");

    const endpoint = getScoresEndpoint + `?tablename=${encodeURIComponent(tableName)}`;
    console.log("get_scores_from_backend - Endpoint = ", endpoint);
    try {
      const response = await fetch(endpoint)
      if (!response.ok) {
        let errorData = { message: response.statusText };
        try {
          errorData = await response.json();
        } catch (e) {

        }
        console.error(`API Error: ${response.status}`, errorData);
        // Throw an error so the calling function knows something went wrong
        throw new Error(`Failed to fetch scores. Status: ${response.status}. Message: ${errorData.message || response.statusText}`);
      }

      console.log("get_scores_from_backend - response = ", response);

      const data = await response.json();
      console.log("Successfully fetched data from API:", data);

      return data;
    } catch (e) {
      console.error("Error in fetchScoresFromAPI:", e.message);

      throw e;
    }
  }





  //Step 0 - Get Started button press. Animate as necessary and navigate to step 1.
  const startGame = async (e) => {
    if (step0_getstarted_ref.current && step0_background_ref.current) {

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'get_started_button_click', {
        });
      } else {
        console.log('gtag not available');
      }



      await step0_getstarted_animate(
        step0_getstarted_ref.current,
        { scale: 0.92, opacity: 1 },
        {
          duration: 0.05,
        }
      )

      await step0_getstarted_animate(
        step0_getstarted_ref.current,
        { scale: 1.0, opacity: 1 },
        {
          duration: 0.1,
          ease: easeIn
        }
      )

      await step0_background_animate(
        step0_background_ref.current,
        { opacity: 0 },
        { duration: 0.25, ease: easeOut }
      )
      setTimeout(() => {
        timerRef.current.hide();
        timeoutTimer.timeoutStart();
        setSessionStep(1);
      }, 50)
    }
  }


  //Step 1 - Submit name button hit - Show username error or start game.
  const submitName = async (e) => {
    timeoutTimer.timeoutReset();
    if (step1_play_ref.current) {
      await step1_play_animate(
        step1_play_ref.current,
        { scale: 0.92, opacity: 1 },
        {
          duration: 0.05,
        }
      )

      await step1_play_animate(
        step1_play_ref.current,
        { scale: 1.0, opacity: 1 },
        {
          duration: 0.1,
          ease: easeIn
        }
      )

      //The name has already been stored.
      if (userName) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'play_button_click', {
          });
        } else {
          console.log('gtag not available');
        }

        await step1_background_animate(
          step1_background_ref.current,
          { opacity: 0 },
          { duration: 0.25, ease: easeOut }
        )

        delay(150);
        setTimeout(() => {
          setSessionStep(2);
          setTimeout(() => {
            timerRef.current.hide();
            initializeGameDataAndBeginTimer();
          }, 50)

        }, 50)

      } else {
        //Change state
        if (userName === "") {
          setUserNameStatus("invalid");
        }
      }
    }
  }




  {/* Listen for question end. */ }
  useEffect(() => {
    if (answeredQuestions) {
      if (gameState === "game") {
        if (unusedQuestions.length > 0) {
          let currentQuestion = unusedQuestions.pop();
          setCurrentQuestion(currentQuestion);
          setEnableTouch(true);
          animateNextQuestion();
        } else {
          animateToBonusPoints();
        }
      }
    }
  }, [answeredQuestions])

  const handleUserNameChange = (event) => {
    timeoutTimer.timeoutReset();

    const originalValue = event.target.value;
    const filteredValue = originalValue.replace(/\d/g, '');
    setUserName(filteredValue);
    if (originalValue !== '' && userNameStatus === 'invalid') {
      setUserNameStatus('default');
    }
  }

  const initializeGameDataAndBeginTimer = () => {
    //Get the current questions, randomize them, and set them to unused.
    //let unusedQuestions = getShuffledQuestions();
    let unusedQuestions = structuredClone(questionData); //Use this for unshuffled
    //Reverse the array so items can be pulled properly.
    unusedQuestions.reverse();
    setUnusedQuestions(unusedQuestions);
    //Pop the first question. This should update the state.
    let currentQuestion = unusedQuestions.pop();
    setCurrentQuestion(currentQuestion);

    advancePreGameTimer(2);
  }


  // Run the 3...2...1...GO Countdown sequence.
  const advancePreGameTimer = async (step) => {
    if (step2_3_ref && step2_2_ref && step2_1_ref && step2_go_ref) {
      //Reset 3
      await step2_3_animate(
        step2_3_ref.current,
        { scale: 0, opacity: 0 },
        { duration: 0 }
      )

      //Reset 2
      await step2_2_animate(
        step2_2_ref.current,
        { scale: 0, opacity: 0 },
        { duration: 0 }
      )

      //Reset 1
      await step2_1_animate(
        step2_1_ref.current,
        { scale: 0, opacity: 0 },
        { duration: 0 }
      )

      //Reset Go
      await step2_go_animate(
        step2_go_ref.current,
        { scale: 0, opacity: 0 },
        { duration: 0 }
      )


      //Animate 3
      await step2_3_animate(
        step2_3_ref.current,
        { scale: 1, opacity: 1 },
        {
          duration: 0.15,
          ease: easeOut,
        }
      )
      await step2_3_animate(
        step2_3_ref.current,
        { scale: 0, opacity: 0 },
        {
          duration: 0.15,
          delay: 0.7,
          ease: easeOut,
        }
      )

      //Animate 2
      await step2_2_animate(
        step2_2_ref.current,
        { scale: 1, opacity: 1 },
        {
          duration: 0.15,
          ease: easeOut,
        }
      )
      await step2_2_animate(
        step2_2_ref.current,
        { scale: 0, opacity: 0 },
        {
          duration: 0.15,
          delay: 0.7,
          ease: easeOut,
        }
      )

      //Animate 1
      await step2_1_animate(
        step2_1_ref.current,
        { scale: 1, opacity: 1 },
        {
          duration: 0.15,
          ease: easeOut,
        }
      )
      await step2_1_animate(
        step2_1_ref.current,
        { scale: 0, opacity: 0 },
        {
          duration: 0.15,
          delay: 0.7,
          ease: easeOut,
        }
      )

      //Animate GO
      await step2_go_animate(
        step2_go_ref.current,
        { scale: 1, opacity: 1 },
        {
          duration: 0.0,
        }
      )

      await delay(750);

      await step2_background_animate(
        step2_background_ref.current,
        { opacity: 0 },
        { duration: 0.25, ease: easeOut }
      )

      setTimeout(() => {
        setGameState("game");
        setShowSecretMenu(false);
        timerRef.current.show();

        setTimeout(() => {
          timerRef.current.start();
        }, 500)

        setSessionStep(8);
      }, 50)

    }
  }


  /* EVENT HANDLERS */
  const handleUserRanOutOfTime = useCallback(() => {

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'times_up', {
      });
    } else {
      console.log('gtag not available');
    }


    console.log("Handle User Ran out of Time!!!!")
    setGameState("outoftime");
    timerRef.current.pause();
  }, []);


  const calculateScore = async (stepNumber) => {

    //Need to fetch from backend.
    let scoresFromBackend = await get_scores_from_backend();
    console.log("scoresFromBackend = ", scoresFromBackend);

    let currentScores = scoresFromBackend.scores;

    console.log("currentScores = ", currentScores);


    //Calculate score.
    var totalPoints = 0;

    for (let i = 0; i < answeredQuestions.length; i++) {
      var totalQuestionPoints = 100;
      var pointsToRemove = 0;
      for (let b = 0; b < answeredQuestions[i].answers.length; b++) {

        if (answeredQuestions[i].answers[b].iscorrect === false) {
          pointsToRemove += 25;
        }
      }

      totalQuestionPoints -= pointsToRemove;
      totalPoints += totalQuestionPoints;
    }


    let bonusPoints = timerRef.current.getCurrentRemainingSeconds();
    totalPoints = totalPoints + bonusPoints;
    setUserTimeBonus(bonusPoints);

    var updatedScoreboard = currentScores;

    let username = userName;
    let userScore = totalPoints;
    let now = new Date();

    let userScoreEntry = {
      id: "",
      date: "",
      name: username,
      score: userScore
    }

    updatedScoreboard.push(userScoreEntry);
    updatedScoreboard.sort((a, b) => {
      return b.score - a.score;
    })

    setScoreboard(updatedScoreboard);
    setUserScore(totalPoints);

    //Upload to backend
    submit_score_to_backend(username, userScore)

    setSessionStep(stepNumber);
  }


  const finishSession = () => {
    setGameState("postgame");
    timerRef.current.hide();
    timerRef.current.pause();
    setTimeout(() => {
      calculateScore(10);
    }, 100)
  }


  {/* ANSWERED QUESTION EVENT HANDLER */ }
  const answeredQuestion = (answerid) => {
    if (!enableTouch) {
      return;
    }
    timeoutTimer.timeoutReset();

    //Disable touch - User should not be able to make another selection until this answer is evaluated.
    setEnableTouch(false);

    //Get an independent reference to the question object
    var updatedQuestion = { ...currentQuestion };

    if (answerid === currentQuestion.answer) {
      updatedQuestion.iscorrect = true;
      for (var i = 0; i < updatedQuestion.answers.length; i++) {
        if (updatedQuestion.answers[i].id === answerid) {
          updatedQuestion.answers[i].iscorrect = true;
          updatedQuestion.answers[i].activeclass = "correct";
        } else {
          // updatedQuestion.answers[i].iscorrect = false;

          if (updatedQuestion.answers[i].activeclass === "unanswered") {
            updatedQuestion.answers[i].activeclass = "inactive_blue";
          } else {
            updatedQuestion.answers[i].activeclass = "inactive_red";
          }
        }
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', currentQuestion.id + '_button_click', {
          'answer_id': answerid,
        });
      } else {
        console.log('gtag not available');
      }


      setCurrentQuestion(updatedQuestion);
      setGameState("correctanswer");
      timerRef.current.pause();
      //setEnableTouch(true);
    } else {

      updatedQuestion.iscorrect = false;
      //Set Answer to incorrect.
      for (var i = 0; i < updatedQuestion.answers.length; i++) {
        console.log("Before updating incorrect question: iscorrect=", updatedQuestion.answers[i].iscorrect)
        if (updatedQuestion.answers[i].activeclass == "unanswered") {
          if (updatedQuestion.answers[i].id === answerid) {
            updatedQuestion.answers[i].iscorrect = false;
            updatedQuestion.answers[i].activeclass = "incorrect";
          }
        }
      }
      setCurrentQuestion(updatedQuestion);
      setEnableTouch(true);
    }
  }





  {/* Support function for answeredQuestion. */ }
  const animateNextQuestion = async () => {
    if (step8_greenplate_ref.current) {
      await delay(150);
      await step8_greenplate_animate(
        step8_greenplate_ref.current,
        { left: '7.5%' },
        { duration: 0.25, bounce: 0.25, type: 'spring' }
      )
    }
  }





  {/* TIMES UP BUTTON EVENT HANDLER */ }
  const handletimesupbutton = async () => {
    timeoutTimer.timeoutReset();

    if (step8_timesup1_ref.current || step8_timesup2_ref.current) {

      if (step8_timesup1_ref.current) {
        await step8_timesup1_animate(
          step8_timesup1_ref.current,
          { scale: 0.92, opacity: 1 },
          {
            duration: 0.05,
          }
        )

        await step8_timesup1_animate(
          step8_timesup1_ref.current,
          { scale: 1.0, opacity: 1 },
          {
            duration: 0.1,
            ease: easeIn
          }
        )
      }

      if (step8_timesup2_ref.current) {
        await step8_timesup2_animate(
          step8_timesup2_ref.current,
          { scale: 0.92, opacity: 1 },
          {
            duration: 0.05,
          }
        )

        await step8_timesup2_animate(
          step8_timesup2_ref.current,
          { scale: 1.0, opacity: 1 },
          {
            duration: 0.1,
            ease: easeIn
          }
        )
      }

      if (timesup_container_ref.current) {
        await timesup_container_animate(
          timesup_container_ref.current,
          { opacity: 0 },
          { duration: 0.25, ease: easeOut }
        )
      }

      await step8_background_animate(
        step8_background_ref.current,
        { opacity: 0.0 },
        { duration: 0.25, ease: easeOut }
      )

      timerRef.current.hide();

      if (timerbkg_ref.current) {
        await timerbkg_animate(
          timerbkg_ref.current,
          { opacity: 0 },
          { duration: 0.25, ease: easeOut }
        )
      }


      if (typeof window.gtag === 'function') {
        window.gtag('event', 'times_up_button_click', {
        });
      } else {
        console.log('gtag not available');
      }

      setTimeout(() => {
        calculateScore(11);
      }, 50)
    }
  }



  {/* SEE HOW YOU DID EVENT HANDLER */ }
  const seeHowYouDid = async () => {
    timeoutTimer.timeoutReset();

    if (step10_seehowyoudid_ref.current) {

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'see_how_you_did_button_click', {
        });
      } else {
        console.log('gtag not available');
      }



      await step10_seehowyoudid_animate(
        step10_seehowyoudid_ref.current,
        { scale: 0.92, opacity: 1 },
        {
          duration: 0.05,
        }
      )

      await step10_seehowyoudid_animate(
        step10_seehowyoudid_ref.current,
        { scale: 1.0, opacity: 1 },
        {
          duration: 0.1,
          ease: easeIn
        }
      )

      await step10_background_animate(
        step10_background_ref.current,
        { opacity: 0 },
        { duration: 0.25, ease: easeOut }
      )


      setTimeout(() => {
        setTimeout(() => {
          setSessionStep(11);
          timerRef.current.hide();
        }, 200);
      }, 50)


    }
  }



  {/* NEXT QUESTION EVENT HANDLER */ }
  const nextQuestion = async () => {
    timeoutTimer.timeoutReset();
    if (step8_continue_ref.current) {
      await step8_continue_animate(
        step8_continue_ref.current,
        { scale: 0.0, opacity: 0 },
        {
          duration: 0.1,
          ease: easeOut
        }
      )

      //Slide off green box
      await step8_greenplate_animate(
        step8_greenplate_ref.current,
        { left: '-110%' },
        { duration: 0.25, ease: easeOut }
      )

      await step8_greenplate_animate(
        step8_greenplate_ref.current,
        { left: '110%' },
        { duration: 0.0 }
      )

      setTimeout(() => {
        var answeredQuestion = { ...currentQuestion } //Shallow copy of currentQuestion
        var answeredQuestionsArray = [...answeredQuestions] //Shallow copy of answeredQuestions
        answeredQuestionsArray.push(answeredQuestion);
        setAnsweredQuestions(answeredQuestionsArray);
        setGameState("game");
        timerRef.current.resume();
      }, 50)
    }
  }




  {/* HELPER FUNCTION - Animates to bonus points screen. Used in NEXT QUESTION EVENT HANDLER */ }
  const animateToBonusPoints = async () => {

    if (step8_background_ref.current) {

      step8_background_animate(
        step8_background_ref.current,
        { opacity: 0 },
        { duration: 0.25, ease: easeOut }
      )

      if (timerbkg_ref.current) {
        await timerbkg_animate(
          timerbkg_ref.current,
          { opacity: 0 },
          { duration: 0.25, ease: easeOut }
        )
      }
      finishSession();
    }
  }




  {/* HELPER FUNCTION FOR ANIMATIONS. */ }
  function delay(milliseconds) {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
  }




  {/* PLAY AGAIN BUTTON EVENT HANDLER */ }
  const playAgain = async (event) => {
    event.stopPropagation();
    if (step11_playagain_ref.current) {

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'leaderboard_play_again', {
        });
      } else {
        console.log('gtag not available');
      }


      await step11_playagain_animate(
        step11_playagain_ref.current,
        { scale: 0.92, opacity: 1 },
        {
          duration: 0.05,
        }
      )

      await step11_playagain_animate(
        step11_playagain_ref.current,
        { scale: 1.0, opacity: 1 },
        {
          duration: 0.1,
          ease: easeIn
        }
      )

      if (step11_background_ref.current) {
        await step11_background_animate(
          step11_background_ref.current,
          { opacity: 0 },
          {
            duration: 0.25,
            ease: easeOut
          }
        )
      }


      timerRef.current.hide();
      resetGame();
      setSessionStep(2);
      setTimeout(() => {
        initializeGameDataAndBeginTimer();
      }, 150)

    } else {
      timerRef.current.hide();
      resetGame();
      setSessionStep(2);
      setTimeout(() => {
        initializeGameDataAndBeginTimer();
      }, 150)
    }
  }



  {/* GO HOME EVENT HANDLER */ }
  const goHome = async (event) => {
    event.stopPropagation();

    if (step11_home_ref.current) {

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'leaderboard_go_home', {
        });
      } else {
        console.log('gtag not available');
      }

      await step11_home_animate(
        step11_home_ref.current,
        { scale: 0.92, opacity: 1 },
        {
          duration: 0.05,
        }
      )

      await step11_home_animate(
        step11_home_ref.current,
        { scale: 1.0, opacity: 1 },
        {
          duration: 0.1,
          ease: easeIn
        }
      )

      if (step11_background_ref.current) {
        await step11_background_animate(
          step11_background_ref.current,
          { opacity: 0 },
          {
            duration: 0.25,
            ease: easeOut
          }
        )
      }

      setTimeout(() => {
        setSessionStep(0);
        timerRef.current.hide();
        resetUser();
        resetGame();
      }, 50)

    } else {
      setTimeout(() => {
        setSessionStep(0);
        timerRef.current.hide();
        resetUser();
        resetGame();
      }, 50);
    }
  }


  {/* HELPER FUNCTION - Resets user data to ready for new run. */ }
  const resetUser = () => {
    setUserName('');
    setUserNameStatus('default');
  }



  {/* HELPER FUNCTION - Resets all game data to ready for new run. */ }
  const resetGame = () => {
    setEnableTouch(true);
    setUserScore(0);
    setUserTimeBonus(0);
    let questions = structuredClone(questionData);

    setUnusedQuestions(questions);
    setAnsweredQuestions([]);
    setCurrentQuestion({});

    setGameState("pregame")
    timerRef.current.reset();
  }




  {/* CALCULATES POINTS BASED ON CURRENTLY ANSWERED QUESTION STATUS */ }
  const getQuestionScore = useCallback(() => {
    let points = 100;
    for (let i = 0; i < currentQuestion.answers.length; i++) {
      if (currentQuestion.answers[i].iscorrect === false) {
        points = points - 25;
      }
    }
    return points;
  });




  {/* SECRET BUTTON EVENT HANDLER */ }
  const handleSecretButtonClick = useCallback(() => {
    console.log("handleSecretButtonClick - Clicked with game state: ", gameState);

    timerRef.current.pause();
    setShowSecretMenu(true);
  });


  {/* SECRET BUTTON CANCEL EVENT HANDLER */ }
  const handleSecretButtonCancel = useCallback(() => {
    console.log("handleSecretButtonClick - Clicked - With game state: ", gameState);

    //Should only resume if the timer was running prior to the secret menu being launched.
    if (gameState != "correctanswer") {
      timerRef.current.resume();
    }
    setShowSecretMenu(false);
  });





  //JSX Begins here.
  return (
    <div>
      {/* app-container and sub-app-container are necessary to ensure proper scaling on desktop browser.*/}
      <div className="app-container">
        <div className="sub-app-container">

          {/* Lives outside of an individual step. This ensures they are always 
          available and the timer does not stop when steps are switched. */}
          <TimerComponent ref={timerRef} duration={timerSeconds * 1000} onComplete={handleUserRanOutOfTime} />
          {sessionStep === 8 &&
            <motion.div ref={timerbkg_ref} className="step8_timer" initial={{ opacity: 1 }}><img src={timerBkgImg} style={{ width: '100%', height: '100%' }}></img></motion.div>
          }

          {/* JSX - STEP 0 */}
          {sessionStep === 0 &&
            <div className="screen-background">
              <motion.div className="screen-background-initial step0-background" ref={step0_background_ref} initial={{ top: '-100%' }} animate={{ top: '0%' }} transition={{ ease: easeOut, duration: 0.75, delay: 0.5 }} style={{ overflow: 'hidden' }} >
                <div className="fullscreen-container">
                  <motion.div ref={step0_play_ref} className="step0_play" style={{ x: 1000, filter: "blur(5px)" }}>Play</motion.div>
                  <motion.div ref={step0_fastfacts_ref} className="step0_fastfacts" style={{ x: 1000, filter: "blur(5px)" }}>Fast Facts</motion.div>
                  <motion.div ref={step0_getstarted_ref} className="defaultbutton step0_getstarted" onClick={startGame} style={{ scale: 0.0, opacity: 0 }}>Get started  »</motion.div>
                </div>
              </motion.div>
            </div>
          }

          {/* Preloading step 0 - These wont' show. But they will help white flash a bit. */}
          {sessionStep === 0 &&
            <div className="hidden-background step1-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step2-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step3-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step4-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step5-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step8-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step10-background" />
          }
          {sessionStep === 0 &&
            <div className="hidden-background step11-background" />
          }




          {/*  JSX - STEP 1  */}
          {sessionStep === 1 &&
            <div className="screen-background">
              <motion.div
                ref={step1_background_ref}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, ease: easeOut, delay: 0.10 }}
                className="screen-background step1-background">
                <div className="fullscreen-container">

                  <div className="step1_canyoubeattheclock">Can You Beat the Clock?</div>
                  <div className="step1_instructions">Race to answer as many questions as possible in 60 seconds.<br />You must answer each question correctly before advancing.</div>
                  <div className="step1_pointsawarded">Points awarded per correct answer:</div>

                  <motion.div ref={step1_1try_ref} initial={{ scale: 0, opacity: 0 }} className="step1_hexagon_text step1_1try">1 try:<br /><span style={{ fontWeight: 700 }}>100 points</span></motion.div>
                  <motion.div ref={step1_2tries_ref} initial={{ scale: 0, opacity: 0 }} className="step1_hexagon_text step1_2tries">2 tries:<br /><span style={{ fontWeight: 700 }}>75 points</span></motion.div>
                  <motion.div ref={step1_3tries_ref} initial={{ scale: 0, opacity: 0 }} className="step1_hexagon_text step1_3tries">3 tries:<br /><span style={{ fontWeight: 700 }}>50 points</span></motion.div>
                  <motion.div ref={step1_4tries_ref} initial={{ scale: 0, opacity: 0 }} className="step1_hexagon_text step1_4tries">4 tries:<br /><span style={{ fontWeight: 700 }}>25 points</span></motion.div>
                  <div className="step1_fasteryoufinish">The faster you finish, the higher your score!</div>


                  <motion.div
                    className="step1_inputcontainer"
                    initial={{ left: '100%' }}
                    animate={{ left: '0%' }}
                    transition={{ type: "spring", delay: 1.5, stiffness: 500, damping: 25 }}
                    ref={step1_inputblock_ref}>
                    <div className="step1_instructiontext_container">
                      <p className="instructiontext">Enter your first name and last initial to play. <span style={(userNameStatus === 'invalid') ? { color: "#E02020", fontStyle: 'italic' } : { display: 'none' }}>Please enter your name and try again.</span></p>
                    </div>
                    <div className="step1_firstname_inputcontainer">
                      {/*First Name */}
                      <input
                        type="text"
                        id="usernameinput"
                        className={userNameStatus === 'invalid' ? "step0_firstname_input_invalid" : "step0_firstname_input_valid"}
                        placeholder="First Name and Last Initial*"
                        value={userName}
                        onChange={handleUserNameChange}
                        maxLength="20"
                      />
                      <div><p className={(userNameStatus === 'invalid') ? "step1_required_invalid" : "step1_required_valid"}>*Required.</p></div>
                    </div>
                  </motion.div>


                  <motion.div className="defaultbutton"
                    ref={step1_play_ref}
                    style={{
                      left: '27%',
                      top: '76%',
                      width: '45%',
                      height: '8%'
                    }}
                    /*initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      type: "spring",
                      bounce: 1.0,
                      visualDuration: 0.01,
                      stiffness: 500,
                      damping: 15,
                      mass: 1.2,
                      delay: 3.5,
                    }}*/
                    initial={{ left: '100%' }}
                    animate={{ left: '27%' }}
                    transition={{ type: "spring", delay: 1.5, stiffness: 500, damping: 25 }}
                    onClick={submitName}
                  >Play  »</motion.div>
                </div>
              </motion.div>
            </div>
          }




          {/*  JSX - STEP 2  */}
          {sessionStep === 2 &&
            <motion.div
              ref={step2_background_ref}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, ease: easeOut, delay: 0.10 }}
              style={sessionStep === 2 ? {} : { height: 0, width: 0, display: 'none' }}
              className=" screen-background step2-background">
              <div className="countdown_greenplate">
                <div className="step2_countdown_bkg" style={{ aspectRatio: 668 / 704, width: '50%' }}>
                  <motion.div ref={step2_3_ref} initial={{ scale: 0, opacity: 0 }} className="step2_countdown">3</motion.div>
                  <motion.div ref={step2_2_ref} initial={{ scale: 0, opacity: 0 }} className="step2_countdown">2</motion.div>
                  <motion.div ref={step2_1_ref} initial={{ scale: 0, opacity: 0 }} className="step2_countdown">1</motion.div>
                  <motion.div ref={step2_go_ref} initial={{ scale: 0, opacity: 0 }} className="step2_countdown">GO</motion.div>
                </div>
              </div>
            </motion.div>
          }



          {/*  JSX - STEP 8  */}
          {sessionStep === 8 &&
            <motion.div
              ref={step8_background_ref}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, ease: easeOut, delay: 0.10 }}
              className=" screen-background step8-background">

              {!showSecretMenu &&
                <div style={{
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'row',
                  height: '19%',
                  width: '37%',
                  left: "30%",
                  top: '0%',
                  zIndex: 300
                }} onClick={() => { handleSecretButtonClick() }}></div>
              }

              {showSecretMenu &&
                <div onClick={handleSecretButtonCancel} className="secretpopoverbackround">
                  <div className="secretpopover">
                    <div className="playagainbutton"
                      style={{
                        left: '5%',
                        top: '10%',
                        width: '90%',
                        height: '20%',
                      }}
                      onClick={playAgain}
                    >{"Play again »"}</div>

                    <div className="homebutton"
                      style={{
                        left: '5%',
                        top: '40%',
                        width: '90%',
                        height: '20%'
                      }}
                      onClick={goHome}
                    >Home »</div>

                    <div className="homebutton"
                      style={{
                        left: '5%',
                        top: '70%',
                        width: '90%',
                        height: '20%'
                      }}
                      onClick={handleSecretButtonCancel}
                    >Cancel</div>
                  </div>
                </div>
              }


              {/* PUT ALL THINGS IN THIS CONTAINER IN THE MORNING ON 05-11-25 */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25, ease: easeOut }}
                ref={step8_greenplate_ref}
                className="greenplate">


                {/* Question # */}
                {!currentQuestion.iscorrect &&
                  <div style={{
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    left: '0%',
                    top: "8%",
                    width: "100%",
                    height: "11%",
                    zIndex: "3",
                    color: "#005DB9",
                    fontSize: "3.5cqh",
                    fontFamily: "acumin-pro,sans-serif",
                    fontWeight: "700"
                  }} >{"Question " + (answeredQuestions.length + 1)}</div>

                }

                {currentQuestion.iscorrect &&
                  <div style={{
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    left: '0%',
                    top: "8%",
                    width: "100%",
                    height: "11%",
                    zIndex: "3",
                    fontSize: "3.5cqh",
                    fontFamily: "acumin-pro,sans-serif",
                    fontWeight: "700",
                    color: '#00953B',
                  }} >{"That's Correct!"}</div>
                }

                {currentQuestion.iscorrect &&
                  <div style={{
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    left: '0%',
                    top: "18%",
                    width: "100%",
                    height: "11%",
                    zIndex: "3",
                    fontSize: "2cqh",
                    fontFamily: "acumin-pro,sans-serif",
                    color: 'black'
                  }} ><span style={{ marginRight: 0, color: 'black', }}>+</span><span style={{ fontWeight: 700, color: 'black', marginRight: 5 }}>{getQuestionScore()}</span> <span> points</span></div>
                }



                {/* Question Text */}
                {!currentQuestion.iscorrect &&
                  <div style={{
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'start',
                    textAlign: 'center',
                    top: "30%",
                    width: "100%",
                    paddingLeft: "5%",
                    paddingRight: "5%",
                    height: "11%",
                    zIndex: "3",
                    color: "black",
                    fontSize: "1.6cqh",
                    fontSize: "2.2cqmin",
                    fontFamily: "acumin-pro,sans-serif",
                  }}
                    dangerouslySetInnerHTML={{ __html: currentQuestion.questiontext }}></div>
                }

                {currentQuestion.iscorrect &&
                  <div style={{
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'start',
                    textAlign: 'center',
                    top: "30%",
                    width: "100%",
                    paddingLeft: "5%",
                    paddingRight: "5%",
                    height: "11%",
                    zIndex: "3",
                    color: "black",
                    fontSize: "1.6cqh",
                    fontSize: "2.2cqmin",
                    fontFamily: "acumin-pro,sans-serif",
                  }}
                    dangerouslySetInnerHTML={{ __html: currentQuestion.questiontextfull }} ></div>
                }


                {/* Answers */}

                <div
                  ref={answers_scope_ref}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    top: "55%",
                    width: "100%",
                    left: '0%',
                    height: "33%",
                    zIndex: "3",
                    fontSize: 18,
                    padding: 0,
                  }}>


                  {currentQuestion.answers.map((answer, index, array) => {
                    const isLast = index === array.length - 1;
                    return (
                      <React.Fragment key={index}>
                        <motion.div
                          whileTap={answer.activeclass != "unanswered" ? {} : { scale: 0.95 }}
                          transition={{ duration: 0.1 }}
                          onClick={() => { answeredQuestion(answer.id) }}
                          className={answer.activeclass}
                          style={{ aspectRatio: '168/188', width: "23.3125%" }}
                          dangerouslySetInnerHTML={{ __html: answer.text }}
                        >
                        </motion.div>
                      </React.Fragment>
                    )
                  })}
                </div>



              </motion.div>



              {/* Try Again */}
              {currentQuestion.iscorrect === false &&
                <motion.div style={{
                  position: 'absolute',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  left: '20%',
                  top: "86%",
                  width: "60%",
                  minHeight: 20,
                  zIndex: "3",
                  color: "black",
                  fontSize: "1.6cqh",
                  fontFamily: 'acumin-pro,sans-serif',
                  fontWeight: 'bold',
                  color: "#E02020"
                }} >{"Try again."}</motion.div>
              }


              {currentQuestion.iscorrect === true &&

                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", bounce: 0.5, duration: 0.5 }}
                  ref={step8_continue_ref}
                  className="correctbutton"
                  style={{
                    left: '39.5%',
                    top: '88%',
                    width: '20%',
                    height: '3.7%'
                  }}
                  onClick={nextQuestion}
                >Continue  »</motion.div>
              }


              {/* Times up  */}
              {timerRef.current.getDisplayTime() === "0:00" &&
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25, ease: easeOut }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    zIndex: 299,
                  }}>
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, type: 'spring', bounce: 0.4, delay: 0.35 }}
                    className="greenplate">
                    <div style={{
                      position: 'absolute',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      left: '0%',
                      top: "25%",
                      width: "100%",
                      height: "11%",
                      zIndex: "3",
                      color: "blue",
                      fontSize: "4cqh",
                      fontFamily: "acumin-pro,sans-serif",
                      fontWeight: "bold",
                      color: '#00953B'
                    }} >{"Time's Up!"}</div>

                    <motion.div
                      style={{
                        position: 'absolute',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        left: '0%',
                        top: "40%",
                        width: "100%",
                        height: "11%",
                        zIndex: "3",
                        color: "blue",
                        fontSize: "2.5cqh",
                        fontFamily: "acumin-pro,sans-serif",
                        fontWeight: "bold",
                        color: 'black',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        paddingLeft: '8%',
                        paddingRight: '8%',
                      }} >{"Your total score is based on the questions you were able to answer correctly."}</motion.div>


                    <motion.div
                      ref={step8_timesup1_ref}
                      className="timesupbutton"
                      style={{
                        left: '23%',
                        top: '59%',
                        width: '53%',
                        height: '16%'
                      }}
                      onClick={handletimesupbutton}
                    >See how you did  »</motion.div>
                  </motion.div>
                </motion.div>
              }


            </motion.div>

          }




          {/*  JSX - STEP 10  */}
          {sessionStep === 10 &&
            <motion.div
              ref={step10_background_ref}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, ease: easeOut, delay: 0.10 }}
              className=" screen-background step10-background">

              <div style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: '0%',
                top: "52.5%",
                width: "100%",
                height: "11%",
                zIndex: "3",
                fontFamily: "acumin-pro,sans-serif",
                fontWeight: 700,
                fontSize: "4.895cqh",
                color: '#00953B'
              }} ><p>Time Bonus!</p></div>


              <div style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: '0%',
                top: "59%",
                width: "100%",
                height: "11%",
                zIndex: "3",
                fontFamily: "acumin-pro,sans-serif",
                fontWeight: 700,
                fontSize: "2.5cqh",
                color: 'black'
              }} ><p>You get {userTimeBonus} extra points.</p></div>



              <motion.div
                ref={step10_seehowyoudid_ref}
                className="seehowyoudidtimebonusbutton"
                style={{
                  left: '27.5%',
                  top: '76.3%',
                  width: '45%',
                  height: '8%',
                }}
                onClick={seeHowYouDid}
              >See how you did  »</motion.div>
            </motion.div>
          }


          {/*  JSX - STEP 11  */}
          {sessionStep === 11 &&
            <motion.div
              ref={step11_background_ref}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, ease: easeOut, delay: 0.10 }}
              className="screen-background step11-background">

              <motion.div className="step11_leaderboard_1" initial={{ left: '100%' }} animate={{ left: '5%' }} transition={{ type: "spring", duration: 0.25, ease: easeOut }}>
                <motion.p className="step11-leaderboard-text" initial={{ left: '-12%', opacity: 0 }} animate={{ left: '0%', opacity: 1 }} transition={{ type: "spring", duration: 0.25, ease: easeOut, bounce: 0.3, delay: 0.25 }}>{scoreboard[0].name} {scoreboard[0].score}</motion.p>
              </motion.div>

              <motion.div className="step11_leaderboard_2" initial={{ left: '100%' }} animate={{ left: '5%' }} transition={{ type: "spring", duration: 0.25, delay: 0.25, ease: easeOut }}>
                <motion.p className="step11-leaderboard-text" initial={{ left: '-12%', opacity: 0 }} animate={{ left: '0%', opacity: 1 }} transition={{ type: "spring", duration: 0.25, ease: easeOut, bounce: 0.3, delay: 0.5 }}>{scoreboard[1].name} {scoreboard[1].score}</motion.p>
              </motion.div>

              <motion.div className="step11_leaderboard_3" initial={{ left: '100%' }} animate={{ left: '5%' }} transition={{ type: "spring", duration: 0.25, delay: 0.5, ease: easeOut }}>
                <motion.p className="step11-leaderboard-text" initial={{ left: '-12%', opacity: 0 }} animate={{ left: '0%', opacity: 1 }} transition={{ type: "spring", duration: 0.25, ease: easeOut, bounce: 0.3, delay: 0.75 }}>{scoreboard[2].name} {scoreboard[2].score}</motion.p>
              </motion.div>

              <motion.div className="step11_leaderboard_4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25, delay: 2, ease: easeOut }}>
                <motion.p className="step11-leaderboard-text-green" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25, ease: easeOut, delay: 2.25 }}>{scoreboard[3].name} {scoreboard[3].score}</motion.p>
              </motion.div>

              <motion.div className="step11_leaderboard_5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25, delay: 2, ease: easeOut }}>
                <motion.p className="step11-leaderboard-text-green" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25, ease: easeOut, delay: 2.25 }}>{scoreboard[4].name} {scoreboard[4].score}</motion.p>
              </motion.div>




              <div style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: '0%',
                top: "20%",
                width: "100%",
                height: "11%",
                zIndex: "3",
                fontFamily: "acumin-pro,sans-serif",
                fontWeight: 700,
                fontSize: 30,
                fontSize: "3cqh",
                color: '#00953B'
              }} ><p>Your final score is {userScore} points!</p></div>

              <div style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                left: '0%',
                top: "25.4%",
                width: "100%",
                height: "11%",
                zIndex: "3",
                fontFamily: "acumin-pro,sans-serif",
                fontSize: "1.6cqh",
                color: 'black',
                textAlign: 'center'
              }} ><p><span style={{ fontWeight: 700, lineHeight: '1cqh' }}>Want to improve your score?</span> Explore the touch screens in the booth or talk<br />with one of our representatives to learn more about WAKIX.</p></div>

              {/* Play Again and Home Buttons */}
              <div
                ref={step11_playagain_ref}
                className="playagainbutton"
                style={{
                  left: '29%',
                  top: '80%',
                  width: '20%',
                  height: '3.5%'
                }}
                onClick={playAgain}
              >{"Play again »"}</div>


              <div
                ref={step11_home_ref}
                className="homebutton"
                style={{
                  left: '51.2%',
                  top: '80%',
                  width: '20%',
                  height: '3.5%'
                }}
                onClick={goHome}
              >Home »</div>


              <div className="step11footer" />
            </motion.div>
          }

        </div>
      </div >
    </div>
  );




}

export default App;
